import React from "react";
import { Select, MenuItem, FormControl, InputLabel, Box } from "@mui/material";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Month + Year selector dropdowns.
 *
 * Props:
 *   month         {number}   1-12
 *   year          {number}
 *   onMonthChange {fn}
 *   onYearChange  {fn}
 *   disabled      {boolean}
 *   disablePast   {boolean}  When true, restricts selection to current and future months only.
 */
const MonthYearSelector = ({ month, year, onMonthChange, onYearChange, disabled = false, disablePast = false }) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 1-based

  // Show only current year and future years when disablePast is set
  const years = disablePast
    ? Array.from({ length: 3 }, (_, i) => currentYear + i)
    : Array.from({ length: 3 }, (_, i) => currentYear - 1 + i);

  // A month is disabled when disablePast is on and the selected year is the current year
  // and the month is before the current month
  const isMonthDisabled = (monthIndex) => {
    if (!disablePast) return false;
    return year === currentYear && monthIndex < currentMonth;
  };

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <FormControl size="small" disabled={disabled} sx={{ minWidth: 130 }}>
        <InputLabel id="jc-month-label" shrink>Month</InputLabel>
        <Select
          labelId="jc-month-label"
          value={month || ""}
          label="Month"
          notched
          onChange={(e) => onMonthChange(parseInt(e.target.value, 10))}
        >
          {MONTH_NAMES.map((name, idx) => (
            <MenuItem key={name} value={idx + 1} disabled={isMonthDisabled(idx + 1)}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" disabled={disabled} sx={{ minWidth: 90 }}>
        <InputLabel id="jc-year-label" shrink>Year</InputLabel>
        <Select
          labelId="jc-year-label"
          value={year || ""}
          label="Year"
          notched
          onChange={(e) => onYearChange(parseInt(e.target.value, 10))}
        >
          {years.map((y) => (
            <MenuItem key={y} value={y}>{y}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default MonthYearSelector;
